import React, { useState, useEffect, useRef, ReactNode, useCallback, useImperativeHandle, forwardRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { 
  AtSign, Search, MessageSquareText, Play, AlignLeft, Brain, Globe, BookOpen, 
  Wrench, Settings2, Clock, LucideIcon, ChevronLeft, Plus, Check
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { STUDY_TOOLS, type StudyTool } from "@/components/chat/chat-input/studyToolsData";
import { ChatInstructionsDialog } from "./ChatInstructionsDialog";

export interface ContextItem {
  id: string;
  label: string;
  icon: LucideIcon;
  iconColor?: string;
  count?: number;
  group: string;
}

export interface ContextGroup {
  name: string;
  items: ContextItem[];
}

export interface AddContextMenuHandle {
  open: () => void;
  close: () => void;
  isOpen: () => boolean;
}

interface AddContextMenuProps {
  groups?: ContextGroup[];
  onSelect?: (item: ContextItem) => void;
  trigger?: ReactNode;
  placeholder?: string;
  emptyMessage?: string;
}

type MenuView = "main" | "study-tools" | "chat-instructions" | "recents";

const defaultGroups: ContextGroup[] = [
  {
    name: "Recents",
    items: [
      { id: "recent-1", label: "Greeting and Introduction", icon: MessageSquareText, group: "Recents" },
      { id: "recent-2", label: "The Strongest Empire...", icon: Play, iconColor: "#ef4444", group: "Recents" },
      { id: "recent-3", label: "LP1_-_Terminology(1)", icon: AlignLeft, group: "Recents" },
    ],
  },
  {
    name: "Context",
    items: [
      { id: "learn-plus", label: "Learn+", icon: Brain, iconColor: "#3cb371", group: "Context" },
      { id: "search", label: "Search", icon: Globe, iconColor: "#3b82f6", group: "Context" },
      { id: "study-mode", label: "Study Mode", icon: BookOpen, iconColor: "#ef4444", group: "Context" },
    ],
  },
  {
    name: "Other Tools",
    items: [
      { id: "study-tools", label: "Study tools", icon: Wrench, count: STUDY_TOOLS.length, group: "Other Tools" },
      { id: "chat-instructions", label: "Chat instructions", icon: Settings2, count: 1, group: "Other Tools" },
      { id: "recents", label: "Recents", icon: Clock, count: 6, group: "Other Tools" },
    ],
  },
];

export const AddContextMenu = forwardRef<AddContextMenuHandle, AddContextMenuProps>(({
  groups = defaultGroups,
  onSelect,
  trigger,
  placeholder = "Search",
  emptyMessage = "No results found.",
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [currentView, setCurrentView] = useState<MenuView>("main");
  const [instructionsDialogOpen, setInstructionsDialogOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Expose methods via ref
  useImperativeHandle(ref, () => ({
    open: () => {
      setIsOpen(true);
      setCurrentView("main");
      setSearch("");
      setHighlightedIndex(0);
    },
    close: () => {
      setIsOpen(false);
      setCurrentView("main");
      setSearch("");
      setHighlightedIndex(0);
    },
    isOpen: () => isOpen,
  }));

  // Get the recents items from groups
  const recentsFromGroups = groups.find(g => g.name === "Recents")?.items || [];

  // Filter items based on search
  const filterItems = useCallback((items: ContextItem[]) => {
    if (!search) return items;
    return items.filter(item => 
      item.label.toLowerCase().includes(search.toLowerCase())
    );
  }, [search]);

  // Get flat list of items for current view (for keyboard navigation)
  const flatItems = useMemo((): ContextItem[] => {
    if (currentView === "main") {
      const filteredGroups = groups
        .map((group) => ({
          ...group,
          items: filterItems(group.items),
        }))
        .filter((group) => group.items.length > 0);
      return filteredGroups.flatMap(group => group.items);
    }
    
    if (currentView === "study-tools") {
      const filteredTools = search
        ? STUDY_TOOLS.filter(tool => tool.label.toLowerCase().includes(search.toLowerCase()))
        : STUDY_TOOLS;
      return filteredTools.map(tool => ({
        id: tool.id,
        label: tool.label,
        icon: tool.icon,
        iconColor: tool.iconColor,
        group: "Study Tools",
      }));
    }
    
    if (currentView === "chat-instructions") {
      const instructions = [
        { id: "study-mode-inst", label: "Study Mode", icon: BookOpen, iconColor: "#ef4444", group: "Chat Instructions" },
      ];
      const createItem = { id: "create-instruction", label: "Create Instruction", icon: Plus, iconColor: "#3b82f6", group: "Chat Instructions" };
      const filtered = search
        ? instructions.filter(i => i.label.toLowerCase().includes(search.toLowerCase()))
        : instructions;
      return [...filtered, createItem];
    }
    
    if (currentView === "recents") {
      return filterItems(recentsFromGroups);
    }
    
    return [];
  }, [currentView, groups, search, filterItems, recentsFromGroups]);

  // Reset highlighted index when items change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [flatItems.length, currentView, search]);

  // Scroll highlighted item into view
  useEffect(() => {
    if (itemRefs.current[highlightedIndex]) {
      itemRefs.current[highlightedIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Calculate position for portal
  useEffect(() => {
    if (isOpen && containerRef.current) {
      const updatePosition = () => {
        if (containerRef.current) {
          const rect = containerRef.current.getBoundingClientRect();
          setPosition({
            top: rect.top,
            left: rect.left,
          });
        }
      };
      updatePosition();
      window.addEventListener("scroll", updatePosition, true);
      window.addEventListener("resize", updatePosition);
      return () => {
        window.removeEventListener("scroll", updatePosition, true);
        window.removeEventListener("resize", updatePosition);
      };
    }
  }, [isOpen]);

  // Focus search input when opening
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen, currentView]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setCurrentView("main");
        setSearch("");
        setHighlightedIndex(0);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case "ArrowDown":
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev < flatItems.length - 1 ? prev + 1 : 0
          );
          break;
        case "ArrowUp":
          event.preventDefault();
          setHighlightedIndex(prev => 
            prev > 0 ? prev - 1 : flatItems.length - 1
          );
          break;
        case "Enter":
          event.preventDefault();
          if (flatItems[highlightedIndex]) {
            handleSelect(flatItems[highlightedIndex]);
          }
          break;
        case "Escape":
          event.preventDefault();
          if (currentView !== "main") {
            setCurrentView("main");
            setSearch("");
            setHighlightedIndex(0);
          } else {
            setIsOpen(false);
            setHighlightedIndex(0);
          }
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, flatItems, highlightedIndex, currentView]);

  const handleSelect = useCallback((item: ContextItem) => {
    // Handle navigation items
    if (item.id === "study-tools") {
      setCurrentView("study-tools");
      setSearch("");
      setHighlightedIndex(0);
      return;
    }
    if (item.id === "chat-instructions") {
      setCurrentView("chat-instructions");
      setSearch("");
      setHighlightedIndex(0);
      return;
    }
    if (item.id === "recents" && item.group === "Other Tools") {
      setCurrentView("recents");
      setSearch("");
      setHighlightedIndex(0);
      return;
    }
    if (item.id === "create-instruction") {
      setIsOpen(false);
      setCurrentView("main");
      setSearch("");
      setHighlightedIndex(0);
      setInstructionsDialogOpen(true);
      return;
    }

    onSelect?.(item);
    setIsOpen(false);
    setCurrentView("main");
    setSearch("");
    setHighlightedIndex(0);
  }, [onSelect]);

  const handleBack = useCallback(() => {
    setCurrentView("main");
    setSearch("");
    setHighlightedIndex(0);
  }, []);

  const handleInstructionSelect = useCallback((instruction: { id: string; name: string; content: string }) => {
    const item: ContextItem = {
      id: instruction.id,
      label: instruction.name,
      icon: Settings2,
      iconColor: "#3b82f6",
      group: "Chat Instructions",
    };
    onSelect?.(item);
    setIsOpen(false);
    setCurrentView("main");
    setSearch("");
    setHighlightedIndex(0);
  }, [onSelect]);

  // Get the index in flatItems for a given item
  const getItemIndex = useCallback((itemId: string): number => {
    return flatItems.findIndex(item => item.id === itemId);
  }, [flatItems]);

  // Render main view
  const renderMainView = () => {
    const filteredGroups = groups
      .map((group) => ({
        ...group,
        items: filterItems(group.items),
      }))
      .filter((group) => group.items.length > 0);

    if (filteredGroups.length === 0) {
      return (
        <div className="py-6 text-center text-sm text-muted-foreground">
          {emptyMessage}
        </div>
      );
    }

    let itemIndex = 0;
    return filteredGroups.map((group, groupIndex) => (
      <React.Fragment key={group.name}>
        {groupIndex > 0 && <div className="-mx-1 h-px bg-border" />}
        <div className="overflow-hidden p-1 text-foreground">
          <div className="px-2 py-1.5">
            <span className="text-sm font-medium text-muted-foreground">
              {group.name}
            </span>
          </div>
          <div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const currentIndex = itemIndex++;
              const isHighlighted = currentIndex === highlightedIndex;
              const isLearnPlus = item.id === "learn-plus";

              const rowContent = (
                <div
                  key={item.id}
                  ref={el => itemRefs.current[currentIndex] = el}
                  onClick={isLearnPlus ? undefined : () => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(currentIndex)}
                  className={`relative flex select-none items-center gap-2.5 rounded-xl px-3 py-2 text-sm outline-none transition-all duration-200 ease-out ${
                    isLearnPlus
                      ? "opacity-60 cursor-default"
                      : `cursor-pointer ${isHighlighted ? "bg-accent" : "hover:bg-accent/50"}`
                  }`}
                >
                  <Icon
                    className="h-4 w-4 flex-shrink-0"
                    style={item.iconColor ? { color: item.iconColor } : undefined}
                  />
                  <span className="truncate text-sm font-medium">
                    {item.label}
                  </span>
                  {isLearnPlus && (
                    <>
                      <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                        Built In
                      </span>
                      <Check className="ml-auto h-4 w-4 text-muted-foreground" />
                    </>
                  )}
                  {!isLearnPlus && item.count !== undefined && (
                    <span className="ml-auto text-sm text-muted-foreground">
                      {item.count}
                    </span>
                  )}
                </div>
              );

              if (isLearnPlus) {
                return (
                  <TooltipProvider key={item.id} delayDuration={300}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        {rowContent}
                      </TooltipTrigger>
                      <TooltipContent side="right" className="text-xs">
                        Learn+ is now built into all chats
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }

              return rowContent;
            })}
          </div>
        </div>
      </React.Fragment>
    ));
  };

  // Render study tools view
  const renderStudyToolsView = () => {
    const filteredTools = search
      ? STUDY_TOOLS.filter(tool => tool.label.toLowerCase().includes(search.toLowerCase()))
      : STUDY_TOOLS;

    return (
      <>
        {/* Header with back button */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <button
            onClick={handleBack}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-primary">Study tools</span>
        </div>

        {/* Search */}
        <div className="flex items-center border-b border-border px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={searchInputRef}
            className="flex w-full rounded-md bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-11 px-1 text-sm"
            placeholder="Search study tools"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Tools list */}
        <div className="overflow-y-auto overflow-x-hidden max-h-[320px] p-1 scrollbar-hide">
          {filteredTools.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No tools found
            </div>
          ) : (
            filteredTools.map((tool, index) => {
              const Icon = tool.icon;
              const item: ContextItem = {
                id: tool.id,
                label: tool.label,
                icon: tool.icon,
                iconColor: tool.iconColor,
                group: "Study Tools",
              };
              const isHighlighted = index === highlightedIndex;
              return (
                <div
                  key={tool.id}
                  ref={el => itemRefs.current[index] = el}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 py-2 text-sm outline-none transition-all duration-200 ease-out ${
                    isHighlighted ? "bg-accent" : "hover:bg-accent/50"
                  }`}
                >
                  <Icon
                    className="h-4 w-4 flex-shrink-0"
                    style={{ color: tool.iconColor }}
                  />
                  <span className="truncate text-sm font-medium">
                    {tool.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </>
    );
  };

  // Render chat instructions view
  const renderChatInstructionsView = () => {
    const instructions = [
      { id: "study-mode-inst", label: "Study Mode", icon: BookOpen, iconColor: "#ef4444", description: "Socratic-style, step-by-step guide" },
    ];

    const filteredInstructions = search
      ? instructions.filter(i => i.label.toLowerCase().includes(search.toLowerCase()))
      : instructions;

    const allItems = [
      ...filteredInstructions.map(i => ({ ...i, group: "Chat Instructions" })),
      { id: "create-instruction", label: "Create Instruction", icon: Plus, iconColor: "#3b82f6", group: "Chat Instructions" },
    ];

    return (
      <>
        {/* Header with back button */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <button
            onClick={handleBack}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-primary">Chat instructions</span>
        </div>

        {/* Search */}
        <div className="flex items-center border-b border-border px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={searchInputRef}
            className="flex w-full rounded-md bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-11 px-1 text-sm"
            placeholder="Search instructions"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Instructions list */}
        <div className="overflow-y-auto overflow-x-hidden max-h-[320px] p-1 scrollbar-hide">
          {allItems.map((instruction, index) => {
            const Icon = instruction.icon;
            const item: ContextItem = {
              id: instruction.id,
              label: instruction.label,
              icon: instruction.icon,
              iconColor: instruction.iconColor,
              group: "Chat Instructions",
            };
            const isHighlighted = index === highlightedIndex;
            return (
              <div
                key={instruction.id}
                ref={el => itemRefs.current[index] = el}
                onClick={() => handleSelect(item)}
                onMouseEnter={() => setHighlightedIndex(index)}
                className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 py-2 text-sm outline-none transition-all duration-200 ease-out ${
                  isHighlighted ? "bg-accent" : "hover:bg-accent/50"
                }`}
              >
                  <Icon
                    className="h-4 w-4 flex-shrink-0"
                    style={{ color: instruction.iconColor }}
                  />
                <span className="truncate text-sm font-medium">
                  {instruction.label}
                </span>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  // Render recents view
  const renderRecentsView = () => {
    const filteredRecents = filterItems(recentsFromGroups);

    return (
      <>
        {/* Header with back button */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2.5">
          <button
            onClick={handleBack}
            className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <span className="text-sm font-medium text-primary">Recents</span>
        </div>

        {/* Search */}
        <div className="flex items-center border-b border-border px-3">
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <input
            ref={searchInputRef}
            className="flex w-full rounded-md bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-11 px-1 text-sm"
            placeholder="Search recents"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Recents list */}
        <div className="overflow-y-auto overflow-x-hidden max-h-[320px] p-1 scrollbar-hide">
          {filteredRecents.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">
              No recents found
            </div>
          ) : (
            filteredRecents.map((item, index) => {
              const Icon = item.icon;
              const isHighlighted = index === highlightedIndex;
              return (
                <div
                  key={item.id}
                  ref={el => itemRefs.current[index] = el}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`relative flex cursor-pointer select-none items-center gap-2.5 rounded-xl px-3 py-2 text-sm outline-none transition-all duration-200 ease-out ${
                    isHighlighted ? "bg-accent" : "hover:bg-accent/50"
                  }`}
                >
                  <Icon
                    className="h-4 w-4 flex-shrink-0"
                    style={item.iconColor ? { color: item.iconColor } : undefined}
                  />
                  <span className="truncate text-sm font-medium">
                    {item.label}
                  </span>
                </div>
              );
            })
          )}
        </div>
      </>
    );
  };

  return (
    <>
      <div ref={containerRef} className="relative">
        {/* Trigger Button */}
        {trigger ? (
          <div onClick={() => setIsOpen(!isOpen)}>{trigger}</div>
        ) : (
          <button
            type="button"
            onClick={() => setIsOpen(!isOpen)}
            className="inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 rounded-full text-primary/60 gap-1.5 h-7 px-2 py-1.5 border border-border focus:ring-0 focus:outline-none hover:text-primary/70 hover:bg-primary/5"
          >
            <AtSign className="h-4 w-4 flex-shrink-0" />
            <span className="text-xs">Add Context</span>
          </button>
        )}

        {/* Menu Popover */}
        {isOpen &&
          createPortal(
            <div
              ref={menuRef}
              className="fixed z-[100] rounded-2xl border border-border bg-popover text-popover-foreground shadow-2xl"
              style={{
                width: 260,
                top: `${position.top}px`,
                left: `${position.left}px`,
                transform: "translateY(calc(-100% - 8px))",
              }}
            >
              <div className="flex h-full flex-col overflow-hidden rounded-2xl bg-popover">
                {currentView === "main" ? (
                  <>
                    {/* Search Input for main view */}
                    <div className="flex items-center border-b border-border px-3">
                      <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                      <input
                        ref={searchInputRef}
                        className="flex w-full rounded-md bg-transparent py-3 outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50 h-11 px-1 text-sm"
                        placeholder={placeholder}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                    </div>

                    {/* List */}
                    <div className="overflow-y-auto overflow-x-hidden max-h-[388px] rounded-b-2xl overscroll-y-none scrollbar-hide">
                      {renderMainView()}
                    </div>
                  </>
                ) : currentView === "study-tools" ? (
                  renderStudyToolsView()
                ) : currentView === "chat-instructions" ? (
                  renderChatInstructionsView()
                ) : (
                  renderRecentsView()
                )}
              </div>
            </div>,
            document.body
          )}
      </div>

      {/* Chat Instructions Dialog */}
      <ChatInstructionsDialog
        open={instructionsDialogOpen}
        onOpenChange={setInstructionsDialogOpen}
        onSelect={handleInstructionSelect}
      />
    </>
  );
});